import React from 'react';
import { Box, Heading, Text, Flex, HStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ShieldCheck } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Status badge — reflects the campaign-wide reviewer verdict
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  approved: {
    label: 'Approved',
    bg: 'rgba(34,197,94,0.10)',
    border: 'rgba(34,197,94,0.28)',
    color: 'rgba(74,222,128,0.9)',
  },
  rejected: {
    label: 'Rejected',
    bg: 'rgba(239,68,68,0.10)',
    border: 'rgba(239,68,68,0.28)',
    color: 'rgba(248,113,113,0.9)',
  },
  pending: {
    label: 'Needs Attention',
    bg: 'rgba(251,191,36,0.10)',
    border: 'rgba(251,191,36,0.28)',
    color: 'rgba(251,191,36,0.9)',
  },
};

const ReviewStatusBadge = ({ verdictStatus }) => {
  const key = verdictStatus === 'approved'
    ? 'approved'
    : verdictStatus === 'rejected'
      ? 'rejected'
      : 'pending';
  const cfg = STATUS_CONFIG[key];
  return (
    <Box
      px={2}
      py="2px"
      borderRadius="full"
      bg={cfg.bg}
      border={`1px solid ${cfg.border}`}
      flexShrink={0}
    >
      <Text
        fontSize="10px"
        fontWeight="700"
        color={cfg.color}
        letterSpacing="0.06em"
        textTransform="uppercase"
      >
        {cfg.label}
      </Text>
    </Box>
  );
};

/**
 * Reviewer-side card shown in the "Shared with me" tab.
 *
 * Visually parallel to `CampaignCard` but intentionally read-only — no
 * rename, no delete, no profile counters. The reviewer is a guest on this
 * campaign and the affordances here reflect that.
 */
const ReviewerCampaignCard = ({ share }) => {
  const navigate = useNavigate();
  const campaignId = share?.campaign_id;
  const campaignName = share?.campaign_name || 'Untitled campaign';
  const ownerName = share?.owner_display_name || 'A GillyReach user';
  const ownerEmail = share?.owner_email || '';
  const acceptedAt = share?.accepted_at;
  const verdictStatus = share?.verdict_status || null;

  const formattedAccepted = acceptedAt
    ? format(new Date(acceptedAt), 'MMM d, yyyy')
    : null;

  const handleClick = () => {
    if (!campaignId) return;
    navigate(`/reviews/${campaignId}`);
  };

  return (
    <Box
      p={4}
      bg="#0f0f0f"
      borderRadius="12px"
      border="1px solid rgba(255,255,255,0.10)"
      transition="all 0.2s"
      cursor="pointer"
      role="group"
      onClick={handleClick}
      _hover={{
        transform: 'translateY(-2px)',
        borderColor: 'rgba(255,255,255,0.22)',
      }}
    >
      <Flex align="center" mb={2} gap={2}>
        <Box
          p="6px"
          borderRadius="6px"
          bg="rgba(65, 105, 225, 0.12)"
          border="1px solid rgba(65, 105, 225, 0.25)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <ShieldCheck size={14} strokeWidth={1.75} color="#7a9aff" />
        </Box>
        <Heading size="md" noOfLines={1} color="#fafafa" fontWeight="600" letterSpacing="-0.01em" flex={1}>
          {campaignName}
        </Heading>
      </Flex>

      <Text color="#a3a3a3" fontSize="xs" mb={3}>
        Shared by{' '}
        <Text as="span" color="rgba(255,255,255,0.85)" fontWeight="500">
          {ownerName}
        </Text>
        {ownerEmail && (
          <Text as="span" color="rgba(255,255,255,0.4)">
            {' '}({ownerEmail})
          </Text>
        )}
      </Text>

      <HStack spacing={2} justify="space-between" align="center">
        <HStack spacing={2}>
          <Box
            px={2}
            py="2px"
            borderRadius="full"
            bg="rgba(122, 226, 161, 0.10)"
            border="1px solid rgba(122, 226, 161, 0.22)"
          >
            <Text fontSize="10px" fontWeight="600" color="rgba(122, 226, 161, 0.85)" letterSpacing="0.06em" textTransform="uppercase">
              Reviewer
            </Text>
          </Box>
          {formattedAccepted && (
            <Text fontSize="10px" color="rgba(255,255,255,0.4)" letterSpacing="0.04em">
              Joined {formattedAccepted}
            </Text>
          )}
        </HStack>
        <ReviewStatusBadge verdictStatus={verdictStatus} />
      </HStack>
    </Box>
  );
};

export default ReviewerCampaignCard;
